import { Injectable } from '@angular/core';
import { ApiBasicService } from './api-basic.service';
import { AppConfiguration } from 'src/app/interfaces/biozoom-api/app-configuration';
import { EMPTY, Observable, concat, interval, of, throwError } from 'rxjs';
import { PreRequestResult } from 'src/app/interfaces/biozoom-api/pre-request-result';
import { catchError, concatMap, delay, delayWhen, filter, repeat, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { AoxMeasurementResult } from 'src/app/interfaces/biozoom-api/aox-measurement-result';
import { ApiProgressUtilsService } from './api-progress-utils.service';
import { PreciseAoxMeasurementResult } from 'src/app/interfaces/biozoom-api/precise-aox-measurement-result';
import { WifiNameResult } from 'src/app/interfaces/biozoom-api/wifi-name-result';
import { ConnectionService } from './connection.service';

@Injectable({
  providedIn: 'root'
})
/**
 * api service for mid-level methods
 * these string together one or two base methods, add format/filter the response
 */
export class ApiMidService {

  constructor(
    private progressUtilsService: ApiProgressUtilsService,
    private apiBasicService: ApiBasicService,
    private connectionService: ConnectionService,
  ) { }


  /**
   * VWS-Prerequest
   * @param config loaded config file
   * @param serial serial ID of the scanner
   * @param measuretype 0->AOX, 1->AOX_MULTI, 2->HRV, 3->HRV-TEST
   * @param initIDX index of the server that is to be contacted
   * @param demoMode if true, return a mock-observable instead of performing the actual request
   * @param retries count of retries performed in the current iteration. this is initialized with '0',
   * and incremented each time a VWS can not be reached.
   * once it has been tried to contact every VWS, and none of them responded, throw an error.
   * @returns observable for the server response
   */
  doPrerequest = (
    config: AppConfiguration,
    serial: string,
    measuretype: number,
    initIDX = 0,
    demoMode = false,
    retries = 0,
    preRequestTimeoutMs?: number,
  ): Observable<PreRequestResult> => {
    return of({}).pipe(
      switchMap(() =>
        of({}).pipe(
          delay(100),
          switchMap(() => this.apiBasicService.doPrerequestSingle(
            config, config.vwsBaseUrls[initIDX], serial, measuretype, demoMode, preRequestTimeoutMs
          )),
          catchError((err) => {
            console.log(`count: ${initIDX}/${config.vwsBaseUrls.length}`);
            const urls = config.vwsBaseUrls;
            urls.splice(2, 0, urls.shift());
            config.vwsBaseUrls = urls;
            if (err instanceof HttpErrorResponse) {
              if (err.status === 404) {
                return throwError('license');
              }
            }
            /* in case NONE of the possible VWS can be reached, abort */
            if (retries > config.vwsBaseUrls.length) {
              retries = 0;
              return throwError(err);
            }
            initIDX = (initIDX + 1) % config.vwsBaseUrls.length;
            retries += 1;
            return EMPTY;
          }),
        )
      ),
      delayWhen(r => interval(Math.max(100, r.new_pre_request_delay_ms))),
      repeat(),
      filter((el: PreRequestResult) => !el.new_pre_request_req),
      take(1),
    );
  }

  /**
   * Resolve a VWS route through one explicitly selected routing server.
   * Unlike doPrerequest, this never rotates to another configured VWS on an
   * error. Callers can therefore reliably control and track the server order.
   */
  doPrerequestOnServer = (
    config: AppConfiguration,
    vwsBaseUrl: string,
    serial: string,
    measuretype: number,
    demoMode = false,
    preRequestTimeoutMs?: number,
  ): Observable<PreRequestResult> => {
    return of({}).pipe(
      delay(100),
      switchMap(() => this.apiBasicService.doPrerequestSingle(
        config, vwsBaseUrl, serial, measuretype, demoMode, preRequestTimeoutMs
      )),
      delayWhen(route => interval(Math.max(100, route.new_pre_request_delay_ms))),
      repeat(),
      filter((route: PreRequestResult) => !route.new_pre_request_req),
      take(1),
    );
  }

  /**
   * upload a single quick aox measurement to the server
   * afterwards, receive the response, and emulate a download progress to display the remaining duration
   * @param serial serial ID of the scanner
   * @param data data of a single aox measurement
   * @param delayMs delay the response by x milliseconds
   * @param recServer ID of the VWS server that is to be used
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  doMeasurementVWS = (
    serial: string,
    data: string,
    delayMs: number,
    recServer: string,
    demoMode = false,
    language = 'de',
    additionalData?: any
  ): Observable<HttpEvent<AoxMeasurementResult>> => {
    return this.apiBasicService.doMeasurementVWSSingle(serial, data, delayMs, recServer, demoMode, language, additionalData).pipe(
      concatMap((event) => {
        if (event.type === HttpEventType.UploadProgress && event.loaded === event.total) {
          // once the upload is done, add a download progress to display the current state
          // tslint:disable-next-line:max-line-length
          return concat(of(event), this.progressUtilsService.generateDownloadProgress<AoxMeasurementResult>(delayMs));
        } else {
          return of(event);
        }
      })
    );
  }

  /**
   * upload all gathered aox responses to calculate the combined result
   * afterwards, receive the response, and emulate a download progress to display the remaining duration
   * @param serial serial ID of the scanner
   * @param delayMs delay the response by x milliseconds
   * @param recServer ID of the VWS server that is to be used
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  doMeasurementVWSPrecise = (
    serial: string,
    delayMs: number,
    recServer: string,
    demoMode = false,
    language = 'de',
    additionalData: {
      series_q: number[],
      series_results: number[]
    }
  ): Observable<HttpEvent<PreciseAoxMeasurementResult>> => {
    return this.apiBasicService.doMeasurementPreciseVWSSingle(serial, delayMs, recServer, demoMode, language, additionalData).pipe(
      concatMap((event) => {
        if (event.type === HttpEventType.UploadProgress && event.loaded === event.total) {
          // once the upload is done, add a download progress to display the current state
          // tslint:disable-next-line:max-line-length
          return concat(of(event), this.progressUtilsService.generateDownloadProgress<PreciseAoxMeasurementResult>(delayMs));
        } else {
          return of(event);
        }
      })
    );
  }

  /**
   * fetch the name of the scanner's Wi-Fi name from the server
   * attempt to do so up to 4 times
   * @param serial serial ID of the scanner
   * @returns observable for the server response
   */
  getWiFiNameWithRetry = (serial: string): Observable<WifiNameResult> => {
    this.apiBasicService.apiCount = 0;
    return this.connectionService.retryRequest(this.apiBasicService.getWiFiName(serial));
  }

}
